import { get, isEqual } from 'lodash-es'

import type { PaginatedData } from '@lib/types/paginatedData'

const DEFAULT_PAGE_SIZE = 500
const MAX_FETCH_LIMIT = 2000

export const fetchAll = async <T>(
  reqFunction: (reqParams: any) => Promise<PaginatedData<T>>,
  reqParams?: any,
  maxLimit = MAX_FETCH_LIMIT,
): Promise<T[]> => {
  reqParams = { ...(reqParams ?? {}), page: 1, page_size: reqParams?.page_size || DEFAULT_PAGE_SIZE }

  const res: PaginatedData<T> = await reqFunction(reqParams)
  const results: T[] = get(res, 'results', [])
  const totalResults = res.count || 0

  if (totalResults > maxLimit) {
    if (window.Sentry) {
      window.Sentry.captureMessage(`Total results exceed ${maxLimit}, results were truncated.`)
    } else {
      console.warn(`Total results exceed ${maxLimit}, results were truncated.`)
    }
  }

  const limitedResults = Math.min(totalResults, maxLimit)

  if (limitedResults <= results.length) return results

  const pages = Math.ceil(limitedResults / reqParams.page_size)
  const promises = Array.from({ length: pages - 1 }, (_, index) => {
    reqParams.page = index + 2
    return reqFunction(reqParams)
  })

  const restOfResults = await Promise.all(promises).then((all) =>
    all.reduce((acc, curr) => acc.concat(curr.results as any), [] as T[]),
  )

  return results.concat(restOfResults)
}

export const fetchAllWithOffset = async <T>(
  reqFunction: (reqParams: any) => Promise<{ pagination: { limit: number; offset: number; total: number }; data: T[] }>,
  reqParams?: any,
  maxLimit = MAX_FETCH_LIMIT,
): Promise<T[]> => {
  reqParams = { ...(reqParams ?? {}), offset: 0, limit: reqParams?.limit || DEFAULT_PAGE_SIZE }

  const res = await reqFunction(reqParams)
  const results: T[] = get(res, 'data', [])
  const totalResults = res.pagination?.total || 0

  if (totalResults > maxLimit) {
    if (window.Sentry) {
      window.Sentry.captureMessage(`Total results exceed ${maxLimit}, results were truncated.`)
    } else {
      console.warn(`Total results exceed ${maxLimit}, results were truncated.`)
    }
  }

  const limitedResults = Math.min(totalResults, maxLimit)

  if (limitedResults <= results.length) return results

  const pages = Math.ceil(limitedResults / reqParams.limit)
  const promises = Array.from({ length: pages - 1 }, (_, index) => {
    reqParams.offset = (index + 1) * reqParams.limit
    return reqFunction(reqParams)
  })

  const restOfResults = await Promise.all(promises).then((all) =>
    all.reduce((acc, curr) => acc.concat(curr.data as any), [] as T[]),
  )

  return results.concat(restOfResults)
}

export async function fetchFirstChunkAndRemainingAsync<T>(
  reqFunction: (reqParams: any) => Promise<PaginatedData<T>>,
  reqParams?: any,
): Promise<[T[], Promise<T[]>]> {
  reqParams = { ...(reqParams ?? {}), page: 1 }

  if (!reqParams.page_size) reqParams.page_size = DEFAULT_PAGE_SIZE

  const res: PaginatedData<T> = await reqFunction(reqParams)
  const firstChunk: T[] = get(res, 'results', [])

  let restChunksPromise: Promise<T[]>

  if (res.count && res.count > firstChunk.length) {
    const pages = Math.ceil(res.count / reqParams.page_size)

    restChunksPromise = Promise.all(
      Array.from({ length: pages - 1 }, (_, index) => {
        reqParams.page = index + 2
        return reqFunction(reqParams).then((res) => get(res, 'results', []))
      }),
    ).then((results) => results.flat()) as Promise<T[]>
  } else {
    restChunksPromise = Promise.all([])
  }

  return [firstChunk, restChunksPromise]
}

export async function urlSafeFetchInChunks<T>(
  ids: (string | number)[],
  fetchFunction: (chunk: (string | number)[]) => Promise<{ results: T[] } | T[]>,
  options: { chunkSize: number } = { chunkSize: 100 },
): Promise<T[]> {
  const idChunks: (string | number)[][] = []
  const { chunkSize } = options
  for (let i = 0; i < ids.length; i += chunkSize) {
    idChunks.push(ids.slice(i, i + chunkSize))
  }

  const fetchPromises = idChunks.map((chunk) => fetchFunction(chunk))
  const results = await Promise.all(fetchPromises)
  return results.flatMap((res) => (Array.isArray(res) ? res : res.results))
}

export function derivePatchRequestFields<T extends object>(
  current: T,
  updated: Partial<T>,
  fieldsString?: string,
): { updatedFields: string[]; payload: Partial<T> } {
  let updatedFields: string[] = []
  const payload: Partial<T> = {}

  if (fieldsString) {
    // Create a list of fields from fieldsString that are also keys in the updated object
    updatedFields = fieldsString
      .split(',')
      .map((f) => f.trim())
      .filter((field) => field in updated)

    // Build the payload based on the filtered updatedFields
    updatedFields.forEach((field) => {
      const key = field as keyof typeof updated
      payload[key] = updated[key]
    })
  } else {
    // If fieldsString is not provided, compare each field in updated to current
    Object.keys(updated).forEach((key) => {
      const typedKey = key as keyof typeof updated
      if (!isEqual(current[typedKey], updated[typedKey])) {
        payload[typedKey] = updated[typedKey]
        updatedFields.push(key)
      }
    })
  }

  // WORKAROUND for iOS: always add the id to updatedFields to get it back in the response
  if (updatedFields.length && !updatedFields.includes('id')) {
    updatedFields.unshift('id')
  }

  return { updatedFields, payload }
}

export async function requestStream(
  url: string,
  body: Record<string, any>,
  token: string,
  onStreamedChunk?: (chunk: string, response: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal, // Add the AbortSignal here
  })

  if (!response.ok || !response.body) {
    throw response
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let result = ''

  try {
    let done = false
    while (!done) {
      const { value, done: doneReading } = await reader.read()
      done = doneReading
      if (value) {
        const chunk = decoder.decode(value, { stream: true })
        result += chunk

        onStreamedChunk?.(chunk, result)
      }

      // Check if the request has been aborted
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }
    }
  } finally {
    reader?.releaseLock()
  }

  return result
}
